import { oddsText } from "@/lib/prefs";
import { Check, Trash2, X } from "lucide-react";
import { RESULT_LABEL, evText, justUpdated, lineText, pct } from "./valueFormat";

export const teamLogo = (code) => (code ? `https://a.espncdn.com/i/teamlogos/nba/500/${code.toLowerCase()}.png` : null);
export const matchup = (p) => (p.home ? `${p.opp} @ ${p.team}` : `${p.team} @ ${p.opp}`);

// Small pulsing pill shown on a pick whose projection just changed after a lineup (5άδα) update.
export function UpdatedBadge({ pick }) {
  if (!justUpdated(pick)) return null;
  return <span className="vs-updated" data-testid={`value-updated-${pick.id}`} title="Το projection άλλαξε μετά από αλλαγή 5άδας">Μόλις ενημερώθηκε</span>;
}

// Live tracker: full-width bar, current total in a bubble, tick at the bookmaker line, check once it wins.
export function PickProgress({ pick }) {
  const pr = pick.progress;
  if (!pr) return null;
  const won = pick.outcome === "won";
  const lost = pick.outcome === "lost";
  const scale = Math.max(pick.line / 0.9, pr.value, 1);
  const at = won && pick.side === "over" ? 100 : Math.min((pr.value / scale) * 100, 100);
  const tick = Math.min((pick.line / scale) * 100, 100);
  const warn = !won && !lost && pick.side === "under" && pr.ratio >= 0.8;
  const tone = won ? "is-won" : lost ? "is-lost" : warn ? "is-warn" : "";
  return (
    <div className={`vs-track ${tone}`} data-testid={`value-progress-${pick.id}`}
      title={pick.side === "over" ? `${pr.value} of ${pr.target} needed` : `${pr.value} — must stay at ${pr.target} or less`}>
      <span className="vs-track-icon">{won ? <Check size={11} strokeWidth={3.5} /> : lost ? <X size={11} strokeWidth={3.5} /> : null}</span>
      <div className="vs-track-bar">
        <i style={{ width: `${at}%` }} />
        {!(won && pick.side === "over") && <span className="vs-track-tick" style={{ left: `${tick}%` }}><small>{pick.line}</small></span>}
        <b className="vs-track-val" style={{ left: `${at}%` }} data-testid={`value-progress-value-${pick.id}`}>{pr.value}</b>
      </div>
    </div>
  );
}

export function PlayerIdentity({ pick, size = "md" }) {
  return (
    <div className={`vs-id is-${size}`}>
      <div className="vs-portrait">
        {pick.photo ? <img src={pick.photo} alt={pick.player_name} loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <span>NBA</span>}
      </div>
      <div className="vs-id-text">
        <div className="vs-id-name">
          <b data-testid={`value-pick-name-${pick.id}`}>{pick.player_name}</b>
          {pick.team && <img className="vs-id-logo" src={teamLogo(pick.team)} alt={pick.team} loading="lazy" />}
          <UpdatedBadge pick={pick} />
        </div>
        <div className="vs-id-sub">
          <b>{pick.team}</b>{pick.position && <><i>|</i><span>{pick.position}</span></>}{pick.jersey && <span>#{pick.jersey}</span>}
        </div>
        <div className="vs-id-sub">{matchup(pick)}{pick.injury ? ` · ${pick.injury}` : ""}</div>
      </div>
    </div>
  );
}

export function Num({ label, value, sub, className = "" }) {
  return <div className={`vs-num ${className}`}><small>{label}</small><b>{value}</b>{sub && <em>{sub}</em>}</div>;
}

export function ValuePickRow({ pick, rank, onDelete }) {
  const id = pick.id;
  return (
    <div className={`vs-row ${pick.stake != null ? "has-stake" : ""} ${pick.result ? `is-${pick.result}` : ""} ${pick.outcome ? `is-${pick.outcome}` : ""}`} data-testid={`value-pick-${id}`} data-outcome={pick.outcome || ""}>
      {rank != null && <span className="vs-rank">{rank}</span>}
      <PlayerIdentity pick={pick} />
      <div className="vs-line" data-testid={`value-pick-line-${id}`}><b>{lineText(pick)}</b><small>@ {oddsText(pick.odds)}</small></div>
      <Num label="PROJ" value={pick.proj} />
      <div title={`Model ${pct(pick.p_model)} · Last ${pick.hist_n} games ${pct(pick.p_hist)}`}><Num label="CHANCE" value={<span data-testid={`value-pick-prob-${id}`}>{pct(pick.p)}</span>} /></div>
      <Num label="FAIR" value={oddsText(pick.fair_odds)} />
      <div className="vs-ev" data-testid={`value-pick-ev-${id}`}>{evText(pick.ev)}</div>
      {pick.stake != null && (
        <Num className="vs-stake" label="STAKE" value={<span data-testid={`value-pick-stake-${id}`}>{pick.stake.toFixed(2)}u</span>} sub={`${(pick.stake_pct * 100).toFixed(1)}%`} />
      )}
      {pick.result ? (
        <span className={`vs-result is-${pick.result}`} data-testid={`value-pick-result-${id}`}>
          {RESULT_LABEL[pick.result]}{pick.actual != null ? ` · ${pick.actual}` : ""}
        </span>
      ) : onDelete ? (
        <button type="button" className="vs-del" onClick={() => onDelete(id)} aria-label="Remove pick" data-testid={`value-pick-delete-${id}`}><Trash2 size={14} /></button>
      ) : <span />}
      <PickProgress pick={pick} />
    </div>
  );
}
